import { useSession } from '@esnafai/shared/auth';
import { formatDate } from '@esnafai/shared/dates';
import { useLiveEvents } from '@esnafai/shared/live';
import { Alert } from '@esnafai/shared/ui';
import { useEffect, useRef, useState } from 'react';
import { useServerOffset } from '@/lib/clock-skew';
import { t } from '@esnafai/shared/i18n';
import { useOnline } from '@/lib/online';
import { useHome } from './use-home';

const RESYNCED_MS = 3_000;
const TRIAL_NOTICE_MS = 7 * 24 * 60 * 60 * 1000;

const billing = { label: t('merchant.shell.banners.billingAction'), to: '/settings/billing' };

// The banners under the header, in order (merchant screens §4.3, §4.4, §4.1): offline, the lost stream and its
// "Güncellendi" flash, the subscription states, the paused assistant, and the clock-skew notice once per session.
export function Banners() {
  const online = useOnline();
  const [resynced, setResynced] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const { polling } = useLiveEvents((event) => {
    if (event.type !== 'resync') return;
    setResynced(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setResynced(false), RESYNCED_MS);
  });
  useEffect(() => () => clearTimeout(timer.current), []);

  const { principal, isOwner } = useSession();
  const merchant = principal && 'merchant' in principal ? principal.merchant : null;
  const home = useHome();
  const { skewed } = useServerOffset();
  const [skewDismissed, setSkewDismissed] = useState(false);

  const trialEndsAt = merchant?.status === 'trial' ? merchant.trial_ends_at : null;
  const trialSoon = trialEndsAt != null && Date.parse(trialEndsAt) - Date.now() <= TRIAL_NOTICE_MS;

  return (
    <div className="flex flex-col gap-2 empty:hidden">
      {!online && <Alert tone="warning">{t('merchant.shell.banners.offline')}</Alert>}
      {polling && <Alert tone="info">{t('merchant.shell.banners.polling')}</Alert>}
      {resynced && <Alert tone="success">{t('merchant.shell.banners.resynced')}</Alert>}
      {isOwner && merchant?.status === 'past_due' && (
        <Alert tone="warning" action={billing}>
          {t('merchant.shell.banners.pastDue')}
        </Alert>
      )}
      {merchant?.status === 'suspended' && (
        <Alert tone="danger" action={isOwner ? billing : undefined}>
          {t('merchant.shell.banners.suspended')}
        </Alert>
      )}
      {isOwner && trialSoon && (
        <Alert tone="info">
          {t('merchant.shell.banners.trialEnding', { date: formatDate(trialEndsAt) })}
        </Alert>
      )}
      {home.data?.ai_paused && (
        <Alert
          tone="warning"
          action={{ label: t('merchant.shell.banners.pausedAllAction'), to: '/ai/status' }}
        >
          {t('merchant.shell.banners.pausedAll')}
        </Alert>
      )}
      {skewed && !skewDismissed && (
        <Alert tone="info" onDismiss={() => setSkewDismissed(true)}>
          {t('merchant.shell.banners.clockSkew')}
        </Alert>
      )}
    </div>
  );
}
